/**
 * Pure edits specific to alcoves (outward bumps on a primary edge): mouth
 * position/width, depth, mouth-wall drags, outer-corner chamfers, and the
 * shared-max-depth chip. All clamp against the alcove constraint ranges.
 */
import {
  alcoveDepthRange,
  alcoveLeftWallRange,
  alcovePositionRange,
  alcoveRightWallRange,
  alcoveWidthRange,
  clamp,
  minAlcoveDepth,
  minAlcoveWidth,
  roundToHalfInch,
} from '../constraints';
import { edgeLeg, primaryEdgeLineFrame } from '../geometry';
import { alcoveDepth, type EdgeName, edgeSegments, type FloorPlanEdgeSegment, type FloorPlanRoom, type OuterWhich, SQUARE_CORNER } from '../schemas';
import { chamferAfterEdit, UNBOUNDED_LEGS } from './chamferLegs';
import { updateHostSegment, updateHostSegments } from './structural';

const peRef = (edge: EdgeName) => ({ k: 'pe', edge } as const);

/** Slide the alcove mouth along its edge (width fixed), between its neighbors. */
export function setAlcovePosition(room: FloorPlanRoom, edge: EdgeName, storageIndex: number, position: number): FloorPlanRoom {
  const r = alcovePositionRange(room, edge, storageIndex);
  return updateHostSegment(room, peRef(edge), storageIndex, a => {
    const p = clamp(roundToHalfInch(position), r.min, r.max);
    return p === a.position ? a : { ...a, position: p };
  });
}

/** Resize the alcove mouth (start fixed), within its width range. */
export function setAlcoveWidth(room: FloorPlanRoom, edge: EdgeName, storageIndex: number, width: number): FloorPlanRoom {
  const r = alcoveWidthRange(room, edge, storageIndex);
  return updateHostSegment(room, peRef(edge), storageIndex, a => {
    const w = clamp(roundToHalfInch(width), r.min, r.max);
    return w === a.width ? a : { ...a, width: w };
  });
}

/** Set the alcove depth (outward; floored at the chamfer-imposed minimum). */
export function setAlcoveDepth(room: FloorPlanRoom, edge: EdgeName, storageIndex: number, depth: number): FloorPlanRoom {
  const r = alcoveDepthRange(room, edge, storageIndex);
  return updateHostSegment(room, peRef(edge), storageIndex, a => {
    const d = Math.max(r.min, roundToHalfInch(depth));
    return d === a.depth ? a : { ...a, depth: d };
  });
}

/** Drag the LEFT mouth wall: position moves, the right wall stays fixed. */
export function setAlcoveLeftWall(room: FloorPlanRoom, edge: EdgeName, storageIndex: number, position: number): FloorPlanRoom {
  const r = alcoveLeftWallRange(room, edge, storageIndex);
  return updateHostSegment(room, peRef(edge), storageIndex, a => {
    const end = a.position + a.width;
    const p = clamp(roundToHalfInch(position), r.min, r.max);
    return p === a.position ? a : { ...a, position: p, width: end - p };
  });
}

/** Drag the RIGHT mouth wall: `end` = new position + width, the left wall stays fixed. */
export function setAlcoveRightWall(room: FloorPlanRoom, edge: EdgeName, storageIndex: number, end: number): FloorPlanRoom {
  const r = alcoveRightWallRange(room, edge, storageIndex);
  return updateHostSegment(room, peRef(edge), storageIndex, a => {
    const e = clamp(roundToHalfInch(end), r.min, r.max);
    const w = e - a.position;
    return w === a.width ? a : { ...a, width: w };
  });
}

/** Slide an alcove (width fixed) so its mouth starts `gap` from the edge span's start. */
export function setAlcoveStartGap(room: FloorPlanRoom, edge: EdgeName, storageIndex: number, gap: number): FloorPlanRoom {
  return setAlcovePosition(room, edge, storageIndex, edgeLeg(room, edge, true) + gap);
}

/** Slide an alcove (width fixed) so its mouth ends `gap` from the edge span's end. */
export function setAlcoveEndGap(room: FloorPlanRoom, edge: EdgeName, storageIndex: number, gap: number): FloorPlanRoom {
  const alcove = edgeSegments(room, edge)[storageIndex];
  if (!alcove) return room;
  const edgeEnd = primaryEdgeLineFrame(room, edge).length - edgeLeg(room, edge, false);
  return setAlcovePosition(room, edge, storageIndex, edgeEnd - gap - alcove.width);
}

/**
 * The "max depth" chip: set the depth of exactly the deepest alcoves on the
 * edge (shallower ones keep their own depth).
 */
export function setAlcovesMaxDepth(room: FloorPlanRoom, edge: EdgeName, depth: number): FloorPlanRoom {
  return updateHostSegments(room, peRef(edge), segments => {
    const als = segments.filter(a => a.edgeSegmentType === 'alcove');
    if (als.length === 0) return segments;
    const md = Math.max(...als.map(alcoveDepth));
    const d = Math.max(0, roundToHalfInch(depth));
    let changed = false;
    const next = segments.map(a => {
      if (a.edgeSegmentType !== 'alcove' || Math.abs(alcoveDepth(a) - md) >= 1e-6 || a.depth === d) return a;
      changed = true;
      return { ...a, depth: d };
    });
    return changed ? next : segments;
  });
}

/**
 * Set one or both legs of an alcove's OUTER corner chamfer (created if
 * missing, legs ≥ 0). The alcove then grows to keep itself valid - width to
 * fit both along-legs, depth to cover the deeper perp-leg - matching the
 * normalize backstop today's panel edit relies on.
 */
export function setAlcoveOuterChamfer(
  room: FloorPlanRoom,
  edge: EdgeName,
  storageIndex: number,
  which: OuterWhich,
  legs: { dx?: number; dy?: number },
): FloorPlanRoom {
  return updateHostSegment(room, peRef(edge), storageIndex, a => {
    const current = a[which] ?? SQUARE_CORNER;
    const chamfer = chamferAfterEdit(current, legs, UNBOUNDED_LEGS);
    if (chamfer === current && a[which]) return a;
    const next: FloorPlanEdgeSegment = { ...a, [which]: { ...chamfer } };
    next.width = Math.max(minAlcoveWidth(next, edge), next.width);
    next.depth = Math.max(minAlcoveDepth(next, edge), alcoveDepth(next));
    return next;
  });
}
